import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sendShipmentEmail } from '../../../utils/emailService';
import { COLORS } from '../../../utils/colors';
import StateAutocomplete from './StateAutocomplete';
import './ShipmentForm.css';
import { sendShipmentToSlack } from '../../../utils/slackService';

const ShipmentForm = () => {
  const [formData, setFormData] = useState({
    pickupLocation: '',
    dropoffLocation: '',
    pickupDate: null,
    email: '',
    phone: '',
    consentTransactional: false,
    consentMarketing: false,
    consentTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Auto-dismiss message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    
    // Apply phone number masking
    if (name === 'phone') {
      finalValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      pickupDate: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Check if consent_terms is checked
    if (!formData.consentTerms) {
      setMessage({ type: 'error', text: 'You must agree to the Terms and Conditions and Privacy Policy.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    const emailData = {
      pickup_location: formData.pickupLocation,
      dropoff_location: formData.dropoffLocation,
      book_pick_date: formData.pickupDate ? formData.pickupDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '',
      email_address: formData.email,
      phone_number: formData.phone,
      consentTransactional: formData.consentTransactional,
      consentMarketing: formData.consentMarketing,
      consentTerms: formData.consentTerms,
    };

    const emailResult = await sendShipmentEmail(emailData);
    const slackResult = await sendShipmentToSlack(emailData);

    if (emailResult.success && slackResult.success) {
      setMessage({ type: 'success', text: 'Your request has been submitted successfully!' });
      // Reset form
      setFormData({
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: null,
        email: '',
        phone: '',
        consentTransactional: false,
        consentMarketing: false,
        consentTerms: false,
      });
    } else if (!emailResult.success) {
      setMessage({ type: 'error', text: `Failed to send email, Please try again later.` });
      //setMessage({ type: 'error', text: `Failed to send email: ${emailResult.error?.text || 'Unknown error'}` });
    } else if (!slackResult.success) {
      setMessage({ type: 'error', text: `Email sent, but failed to complete process.` });
      //setMessage({ type: 'error', text: `Email sent, but failed to notify Slack: ${slackResult.error?.message || 'Unknown error'}` });
    }

    setLoading(false);
  };

  return (
    <form className="request-form ftco-animate" onSubmit={handleSubmit}>
      <h2>Get an instant quote</h2>
      <h2>or call now <span style={{ color: COLORS.primary }}>+1 (888) 512-NEST</span></h2>
      <hr style={{ margin: '15px 0', borderColor: COLORS.textPrimary, borderWidth: '2px' }} />
      <div className="form-group">
        <label htmlFor="pickup_location" className="label">Pick-up location</label>
        <StateAutocomplete
          value={formData.pickupLocation}
          onChange={handleChange}
          id="pickup_location"
          name="pickupLocation"
          placeholder="City, State"
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="dropoff_location" className="label">Drop-off location</label>
        <StateAutocomplete
          value={formData.dropoffLocation}
          onChange={handleChange}
          id="dropoff_location"
          name="dropoffLocation"
          placeholder="City, State"
          required
        />
      </div>
      <div className="d-flex">
        <div className="form-group mr-2">
          <label htmlFor="book_pick_date" className="label">Pick-up date</label>
          <DatePicker
            selected={formData.pickupDate}
            onChange={handleDateChange}
            dateFormat="MM/dd/yyyy"
            className="form-control"
            placeholderText="Date"
            id="book_pick_date"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone_number" className="label">Phone Number</label>
          <input
            type="text"
            className="form-control"
            id="phone_number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            required
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="email_address" className="label">Email Address</label>
        <input
          type="email"
          className="form-control"
          id="email_address"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="example@email.com"
          required
        />
      </div>
      <div className="form-group">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="consent_transactional"
            name="consentTransactional"
            checked={formData.consentTransactional}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="consent_transactional" style={{ fontSize: '12px' }}>
            By checking this box and clicking "SUBMIT" you consent to receive transactional text messages for notifications from NEST Auto Transport.
          </label>
        </div>
        <div className="form-check mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="consent_marketing"
            name="consentMarketing"
            checked={formData.consentMarketing}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="consent_marketing" style={{ fontSize: '12px' }}>
            By checking this box and clicking "SUBMIT" you consent to receive marketing text messages for promotional offers from NEST Auto Transport.
          </label>
        </div>
        <div className="form-check mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="consent_terms"
            name="consentTerms"
            checked={formData.consentTerms}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="consent_terms" style={{ fontSize: '12px' }}>
            I agree to the <a href="/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </label>
        </div>
      </div>
      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '15px' }}>
          <span>{message.text}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setMessage({ type: '', text: '' })}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              opacity: 0.9,
              padding: '0 2px',
              marginLeft: '10px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            ×
          </button>
        </div>
      )}
      <div className="form-group">
        <input
          type="submit"
          id="shipmentSubmit"
          value={loading ? 'Sending...' : 'Submit'}
          className="btn btn-primary py-3 px-4"
          disabled={loading || !formData.consentTerms}
        />
      </div>
    </form>
  );
};

export default ShipmentForm;


